export type LocalDateTimeError = "required" | "invalid" | "nonexistent" | "ambiguous";
export type LocalDateTimeResult =
  | { iso: string; error: null }
  | { iso: null; error: LocalDateTimeError };

const pad = (value: number, length = 2) => String(value).padStart(length, "0");

/** A datetime-local field contains wall time in the browser's zone, never a UTC suffix. */
export function toLocalDateTimeInput(value: string | Date): string {
  const date = typeof value === "string" ? new Date(value) : value;
  if (!Number.isFinite(date.getTime()) || date.getFullYear() < 1 || date.getFullYear() > 9999) return "";
  return `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * Preserve the API instant verbatim when its displayed minute is unchanged.
 * This keeps sub-minute precision and either occurrence of an existing DST fold.
 * New/changed values must identify exactly one instant; Date's silent gap/fold
 * normalization is not an acceptable scheduling decision.
 */
export function resolveLocalDateTime(value: string, originalIso?: string | null): LocalDateTimeResult {
  if (!value) return { iso: null, error: "required" };
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) return { iso: null, error: "invalid" };
  const [year, month, day, hour, minute] = match.slice(1).map(Number);
  const calendar = new Date(0);
  calendar.setUTCFullYear(year, month - 1, day);
  calendar.setUTCHours(hour, minute, 0, 0);
  if (year < 1 || calendar.getUTCFullYear() !== year || calendar.getUTCMonth() !== month - 1
    || calendar.getUTCDate() !== day || calendar.getUTCHours() !== hour || calendar.getUTCMinutes() !== minute) {
    return { iso: null, error: "invalid" };
  }
  if (originalIso && /(?:Z|[+-]\d{2}:\d{2})$/i.test(originalIso) && toLocalDateTimeInput(originalIso) === value) {
    return { iso: originalIso, error: null };
  }
  const date = new Date(value);
  if (toLocalDateTimeInput(date) !== value) return { iso: null, error: "nonexistent" };

  // Check alternatives at the editor's minute precision, including half-hour
  // folds and date-line changes. Run only at submission, never while typing.
  const sameMinute = (candidate: Date) => candidate.getFullYear() === year
    && candidate.getMonth() === month - 1 && candidate.getDate() === day
    && candidate.getHours() === hour && candidate.getMinutes() === minute;
  for (let delta = 1; delta <= 26 * 60; delta += 1) {
    if (sameMinute(new Date(date.getTime() + delta * 60_000))
      || sameMinute(new Date(date.getTime() - delta * 60_000))) {
      return { iso: null, error: "ambiguous" };
    }
  }
  return { iso: date.toISOString(), error: null };
}

export function localDateTimeErrorMessage(error: LocalDateTimeError, language: "ar" | "en"): string {
  const messages: Record<LocalDateTimeError, { ar: string; en: string }> = {
    required: { ar: "أدخل التاريخ والوقت.", en: "Enter a date and time." },
    invalid: { ar: "أدخل تاريخًا ووقتًا صالحين.", en: "Enter a valid date and time." },
    nonexistent: {
      ar: "هذا الوقت غير موجود بسبب تغيير التوقيت المحلي. اختر وقتًا آخر.",
      en: "This time does not exist because the local clock changes. Choose another time."
    },
    ambiguous: {
      ar: "هذا الوقت يتكرر بسبب تغيير التوقيت المحلي. اختر وقتًا غير ملتبس.",
      en: "This time occurs twice because the local clock changes. Choose an unambiguous time."
    }
  };
  return messages[error][language];
}

export function localTimeZoneHint(language: "ar" | "en"): string {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return language === "ar" ? `التوقيت المحلي للجهاز: ${zone}` : `Device local time: ${zone}`;
}
